import React from "react";
import { Box, Typography, Avatar, CircularProgress } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";

const HabitCard = ({ title, target, progress, icon, color, actualValue }) => {
  // Parse target to handle thousands separator and convert to number
  const parsedTarget = parseInt(target.replace(/,/g, ""), 10); // Remove commas and parse as number

  // Format actual steps with commas
  const formattedActualValue = actualValue
    ? actualValue.toLocaleString("en-US")
    : "";

  // Calculate dynamic progress based on actualValue and parsed target
  const dynamicProgress =
    title === "Steps" && actualValue
      ? Math.min((actualValue / parsedTarget) * 100, 100) // Cap progress at 100%
      : progress;

  // Adjust gradient for light visibility and maintain 60% focus
  const adjustedProgress = Math.min(dynamicProgress, 95);
  const gradientColor = `linear-gradient(135deg, ${color} 0%, rgba(255, 255, 255, 0.5) ${adjustedProgress}%, #f9f9f9 100%)`;

  return (
    <Box
      sx={{
        width: "100%",
        height: "160px",
        margin: "5px",
        background: gradientColor,
        borderRadius: "12px",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
        display: "flex",
        flexDirection: "column",
        padding: "12px",
        position: "relative",
        overflow: "hidden",
        transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
        "&:hover": {
          transform: "scale(1.05)",
          boxShadow: "0 8px 16px rgba(0, 0, 0, 0.3)",
        },
      }}
    >
      {/* Top Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          marginBottom: "10px",
          zIndex: 2, // Above gradient
        }}
      >
        {/* Habit Icon */}
        <Avatar
          src={icon}
          sx={{
            width: "36px",
            height: "36px",
            marginRight: "10px",
            border: "2px solid #FFF",
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
          }}
        />
        {/* Title */}
        <Typography
          variant="body1"
          sx={{
            fontSize: "14px",
            fontWeight: "bold",
            color: "#333",
          }}
        >
          {title}
        </Typography>
        {/* Star Icon */}
        <StarIcon
          sx={{
            color: "#FFD700",
            fontSize: "20px",
            marginLeft: "auto",
          }}
        />
      </Box>

      {/* Circular Progress Section */}
      <Box
        sx={{
          position: "relative",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          flexGrow: 1,
          zIndex: 2, // Above gradient
        }}
      >
        {/* Background CircularProgress (Gray) */}
        <CircularProgress
          variant="determinate"
          value={100}
          size={100}
          thickness={5}
          sx={{
            color: "#f0f0f0", // Light gray color for the background circle
            position: "absolute",
          }}
        />
        {/* Foreground CircularProgress (Dynamic Color with Animation) */}
        <CircularProgress
          variant="determinate"
          value={dynamicProgress}
          size={100}
          thickness={5}
          sx={{
            color: color,
            position: "absolute",
            transition: "all 0.5s ease-in-out", // Smooth animation for progress change
          }}
        />
        {/* Display Actual Steps or Percentage */}
        <Typography
          variant="h6"
          sx={{
            position: "absolute",
            fontSize: "16px",
            fontWeight: "bold",
            color: "#333",
          }}
        >
          {title === "Steps" && formattedActualValue
            ? `${formattedActualValue}`
            : `${progress}%`}
        </Typography>
      </Box>

      {/* Bottom Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "10px",
          zIndex: 2, // Above gradient
        }}
      >
        {/* Target */}
        <Typography
          variant="body2"
          sx={{
            fontSize: "12px",
            color: "#666",
          }}
        >
          Target: <strong>{target}</strong>
        </Typography>
      </Box>
    </Box>
  );
};

export default HabitCard;